import React, { useCallback, useRef, useState } from "react";

interface BusinessCardUploaderProps {
  /** Called with the chosen image files. */
  onFilesSelected: (files: File[]) => void;
  /** True while extraction is in flight — disables further input. */
  busy: boolean;
  /** Compact variant for the "add another card" action once results exist. */
  compact?: boolean;
}

const isImage = (file: File) => file.type.startsWith("image/");

/**
 * Multi-photo uploader: file picker + drag-and-drop, with mobile camera capture.
 */
const BusinessCardUploader: React.FC<BusinessCardUploaderProps> = ({
  onFilesSelected,
  busy,
  compact = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      const files = Array.from(fileList).filter(isImage);
      if (files.length > 0) onFilesSelected(files);
    },
    [onFilesSelected]
  );

  const openPicker = () => {
    if (!busy) inputRef.current?.click();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) handleFiles(e.dataTransfer.files);
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      // capture hints mobile browsers to offer the camera directly.
      capture="environment"
      multiple
      className="hidden"
      onChange={(e) => {
        handleFiles(e.target.files);
        // Reset so selecting the same file again re-triggers onChange.
        e.target.value = "";
      }}
    />
  );

  if (compact) {
    return (
      <>
        {hiddenInput}
        <button
          type="button"
          onClick={openPicker}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg border border-sagelyn-border bg-sagelyn-surface px-4 py-2 text-sm font-medium text-sagelyn-foreground transition-colors hover:border-sagelyn-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          + Add another card
        </button>
      </>
    );
  }

  return (
    <div
      onClick={openPicker}
      onDragOver={(e) => {
        e.preventDefault();
        if (!busy) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openPicker();
      }}
      className={[
        "flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-colors",
        dragging
          ? "border-sagelyn-primary bg-sagelyn-primary/10"
          : "border-sagelyn-border bg-sagelyn-surface hover:border-sagelyn-primary",
        busy ? "pointer-events-none opacity-60" : "",
      ].join(" ")}
    >
      {hiddenInput}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="mb-4 h-12 w-12 text-sagelyn-primary"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 16.5V18a2 2 0 002 2h14a2 2 0 002-2v-1.5M7.5 9 12 4.5 16.5 9M12 4.5V15"
        />
      </svg>
      <p className="text-base font-medium text-sagelyn-foreground">
        Tap to take a photo or upload business cards
      </p>
      <p className="mt-1 text-sm text-sagelyn-muted">
        Drag &amp; drop images here — you can add several at once
      </p>
    </div>
  );
};

export default BusinessCardUploader;
